import { defineAsyncComponent } from 'vue';

import { type App } from '~/app';

export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Menu Overlay Component');

    const listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]> = [];

    app.logger.debug('[Stockland FE] Initialise Stockland Menu Overlay RTC Component');
    app.vueApp.component('menu-overlay-rtc', defineAsyncComponent(async () => import("./menu-overlay-rtc.vue")));

    app.afterMount
        .then(() => {
            mountMenuOverlay(listeners);
        })
        .catch((error) => {
            throw new Error('[Stockland FE] Unable to register Stockland Menu Overlay Component', error);
        });

    return function destroyMenuOverlay() {
        unMountMenuOverlay(listeners);

        app.logger.debug('[DXA] Menu Overlay Component destroyed');
    };
};

function mountMenuOverlay(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>,
    root: HTMLElement | null = null
) {
    let rootNode: Document | HTMLElement = document;
    if (root) rootNode = root;

    const menuOverlays = rootNode.querySelectorAll<HTMLElement>('.menu-overlay');

    for (const menuOverlay of menuOverlays) {
        const businessUnits = menuOverlay.querySelectorAll<HTMLElement>('.business-units .business-unit');
        const menuItems = menuOverlay.querySelectorAll<HTMLElement>('.menu-items .menu-item');
        const mobileMenu = menuOverlay.querySelector<HTMLElement>('.mobile-menu');

        for (const businessUnit of businessUnits) {
            const updateActiveBusinessUnit = () => {
                const businessUnitIndex = businessUnit.id;
                const businessUnitMenuitems = menuOverlay.querySelector<HTMLElement>(
                    `.menu-items #menu-item-${businessUnitIndex}`
                );

                // Update active business unit text/chevron style
                for (const businessUnit of businessUnits) {
                    businessUnit.classList.remove('active');
                }

                businessUnit.classList.add('active');

                // Update menu items to new business unit
                for (const menuItem of menuItems) {
                    menuItem.classList.remove('grid');
                    menuItem.classList.add('hidden');
                }

                businessUnitMenuitems?.classList.remove('hidden');
                businessUnitMenuitems?.classList.add('grid');

                businessUnit.addEventListener('click', updateActiveBusinessUnit);
                listeners.push([businessUnit, 'click', updateActiveBusinessUnit]);

                // Update business unit link value after it becomes the active business unit and remove links from all other BUs
                const dataLinkValue = businessUnit.dataset.href;

                for (const businessUnit of businessUnits) {
                    businessUnit.setAttribute('onclick', '');
                }

                setTimeout(() => {
                    if (dataLinkValue) {
                        businessUnit.setAttribute('onclick', `window.location='${dataLinkValue}';`);
                    }
                }, 500);
            };

            businessUnit.addEventListener('click', updateActiveBusinessUnit);
            listeners.push([businessUnit, 'click', updateActiveBusinessUnit]);
        }

        if (mobileMenu) {
            const menuItems = mobileMenu.querySelectorAll<HTMLElement>('.menu-item');
            const bottomBar = mobileMenu.querySelector<HTMLElement>('.bottom-bar');

            for (const menuItem of menuItems) {
                const dropdownButton = menuItem.querySelector<HTMLElement>('.dropdown-button');
                const menuItemCollapse = menuItem.querySelector<HTMLElement>('.mobile-item-collapse');
                const menuSubItemCollapse = menuItem.querySelector<HTMLElement>('.mobile-subitem-collapse');
                const menuLabel = menuItem.querySelector<HTMLElement>('.menu-label');

                if (dropdownButton) {
                    const rotateDropdownButton = () => {
                        const icon = dropdownButton.querySelector<SVGSVGElement>('svg');
                        icon?.classList.toggle('rotate-180');
                    };

                    if (menuLabel) {
                        const menuLabelLink = menuLabel.querySelector<HTMLElement>('a');
                        if (menuLabelLink) {
                            menuLabelLink.addEventListener('click', rotateDropdownButton);
                            listeners.push([menuLabelLink, 'click', rotateDropdownButton]);
                        }
                    }

                    dropdownButton.addEventListener('click', rotateDropdownButton);

                    listeners.push([dropdownButton, 'click', rotateDropdownButton]);
                }

                if (bottomBar) {
                    const bottomBarLinks = bottomBar.querySelectorAll<HTMLElement>('a');

                    for (const link of bottomBarLinks) {
                        // eslint-disable-next-line @typescript-eslint/no-loop-func
                        const handleTouchLink = (event: Event) => {
                            event.preventDefault();
                            const href = link.getAttribute('href');
                            if (href) {
                                window.location.href = href;
                            }
                        };

                        link.addEventListener('touchend', handleTouchLink);
                        listeners.push([link, 'touchend', handleTouchLink]);
                    }
                }

                stopCollapsePropagation(menuItemCollapse);
                stopCollapsePropagation(menuSubItemCollapse);
            }
        }
    }

    function stopCollapsePropagation(element: HTMLElement | null) {
        if (element) {
            // Stop the shown and hide collapse event proprogations to not trigger
            element.addEventListener('show.bs.collapse', (event: Event) => event.stopPropagation());
            listeners.push([element, 'show.bs.collapse', (event: Event) => event.stopPropagation()]);

            element.addEventListener('hide.bs.collapse', (event: Event) => event.stopPropagation());
            listeners.push([element, 'hide.bs.collapse', (event: Event) => event.stopPropagation()]);

            element.addEventListener('shown.bs.collapse', (event: Event) => event.stopPropagation());
            listeners.push([element, 'shown.bs.collapse', (event: Event) => event.stopPropagation()]);

            element.addEventListener('hidden.bs.collapse', (event: Event) => event.stopPropagation());
            listeners.push([element, 'hidden.bs.collapse', (event: Event) => event.stopPropagation()]);
        }
    }
}

export function unMountMenuOverlay(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>
) {
    for (const [element, event, listener] of listeners) {
        element.removeEventListener(event, listener);
    }
}
